import { Download, Edit3, Filter, RefreshCw, X } from 'lucide-react';
import { DashboardTableSearchInput } from '@shigo-ui/dashboard-ui';
import { Button, cn } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { AppRequestDatePreset, type AppRequestsListFilter } from '../../model';

interface AppRequestsTableToolbarProps {
  filter: AppRequestsListFilter;
  activeFilterCount: number;
  filtersOpen: boolean;
  selectedCount: number;
  canEdit: boolean;
  canExport: boolean;
  exportPending: boolean;
  onSearchChange: (value: string) => void;
  onDatePresetChange: (value: AppRequestDatePreset) => void;
  onFiltersOpenChange: (value: boolean) => void;
  onClearFilters: () => void;
  onEditSelected: () => void;
  onRefresh: () => void;
  onExport: () => void;
  publisherScoped?: boolean;
}

export function AppRequestsTableToolbar({
  filter,
  activeFilterCount,
  filtersOpen,
  selectedCount,
  canEdit,
  canExport,
  exportPending,
  onSearchChange,
  onDatePresetChange,
  onFiltersOpenChange,
  onClearFilters,
  onEditSelected,
  onRefresh,
  onExport,
  publisherScoped,
}: AppRequestsTableToolbarProps) {
  const search = useDebouncedValue(filter.searchTerm, onSearchChange, 650);

  return (
    <>
      <DashboardTableSearchInput
        aria-label={dashboardT('supplyApps.searchRequests')}
        placeholder={dashboardT('supplyApps.searchRequests')}
        value={search.draft}
        onChange={(event) => {
          search.setDraft(event.target.value);
        }}
      />
      {!publisherScoped ? (
        <div
          className="bg-muted flex h-9 items-center rounded-md p-1"
          role="group"
          aria-label={dashboardT('supplyApps.request.datePreset')}
        >
          <button
            type="button"
            aria-pressed={filter.datePreset === AppRequestDatePreset.Today}
            className={cn(
              'h-7 cursor-pointer rounded-sm px-3 text-xs font-medium',
              filter.datePreset === AppRequestDatePreset.Today &&
                'bg-card text-foreground shadow-sm',
            )}
            onClick={() => {
              onDatePresetChange(AppRequestDatePreset.Today);
            }}
          >
            {dashboardT('supplyApps.request.today')}
          </button>
          <button
            type="button"
            aria-pressed={filter.datePreset === AppRequestDatePreset.Last7Days}
            className={cn(
              'h-7 cursor-pointer rounded-sm px-3 text-xs font-medium',
              filter.datePreset === AppRequestDatePreset.Last7Days &&
                'bg-card text-foreground shadow-sm',
            )}
            onClick={() => {
              onDatePresetChange(AppRequestDatePreset.Last7Days);
            }}
          >
            {dashboardT('supplyApps.request.last7Days')}
          </button>
        </div>
      ) : null}
      <div className="flex items-center">
        <Button
          type="button"
          variant="outline"
          className={activeFilterCount ? 'rounded-r-none' : undefined}
          aria-label={dashboardT('supplyApps.filters')}
          aria-expanded={filtersOpen}
          aria-controls="app-requests-filter-toolbar"
          onClick={() => {
            onFiltersOpenChange(!filtersOpen);
          }}
        >
          <Filter className="h-4 w-4" aria-hidden="true" />
          {dashboardT('supplyApps.filters')}
          {activeFilterCount ? (
            <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-[10px] font-bold">
              {activeFilterCount}
            </span>
          ) : null}
        </Button>
        {activeFilterCount ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive rounded-l-none border-l-0"
            aria-label={dashboardT('supplyApps.clearFilters')}
            onClick={onClearFilters}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      {!publisherScoped && selectedCount > 0 && canEdit ? (
        <Button type="button" onClick={onEditSelected}>
          <Edit3 className="h-4 w-4" />
          {dashboardT('supplyApps.request.editSelected')} ({selectedCount})
        </Button>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={dashboardT('supplyApps.request.refresh')}
        onClick={onRefresh}
      >
        <RefreshCw className="h-4 w-4" />
      </Button>
      {!publisherScoped && canExport ? (
        <Button type="button" variant="outline" disabled={exportPending} onClick={onExport}>
          <Download className="h-4 w-4" />
          {dashboardT('supplyApps.request.export')}
        </Button>
      ) : null}
    </>
  );
}
